import { useMemo } from 'react';
import { Modal, Typography } from 'antd';
import { aboveEverythingZIndex } from '@/shared/ui/FloatingWindow';
import { DialogFooter } from '@/shared/ui/DialogFooter';
import { ResponsiveTable } from '@/shared/ui/ResponsiveTable';
import type { ResponsiveColumns } from '@/shared/ui/ResponsiveTable';
import { useBreakpoint } from '@/shared/lib/hooks';
import type { PaymentPreviewAffectedDocument, PaymentPreviewBalanceChange, PaymentPreviewDiff } from '@/entities/payment';
import './PaymentPreviewConsequencesDialog.mobile.css';

const PAYMENT_STATUS_LABEL: Record<string, string> = { UNPAID: 'не оплачено', PARTIAL: 'частично', PAID: 'оплачено' };

export interface PaymentPreviewConsequencesDialogProps {
  diff: PaymentPreviewDiff | undefined;
  onResolve: (confirmed: boolean) => void;
}

// `mobile` — роль столбца на карточке телефона; на десктопе `ResponsiveTable` отдаёт обычную таблицу.
const documentColumns: ResponsiveColumns<PaymentPreviewAffectedDocument> = [
  { title: 'Документ', dataIndex: 'label', width: 120, mobile: 'title' },
  { title: 'Оплачено', width: 160, render: (_v, r) => `${r.paidAmountBefore} → ${r.paidAmountAfter}` },
  {
    title: 'Статус оплаты',
    width: 160,
    render: (_v, r) => `${PAYMENT_STATUS_LABEL[r.paymentStatusBefore] ?? r.paymentStatusBefore} → ${PAYMENT_STATUS_LABEL[r.paymentStatusAfter] ?? r.paymentStatusAfter}`,
  },
];

const balanceColumns: ResponsiveColumns<PaymentPreviewBalanceChange> = [
  { title: 'Контрагент', dataIndex: 'counterpartyName', mobile: 'title' },
  { title: 'Было', width: 120, align: 'right', render: (_v, r) => r.before },
  { title: 'Станет', width: 120, align: 'right', render: (_v, r) => r.after },
];

/** §8.5: последствия правки/разнесения/удаления платежа — предупреждение, а не блокировка
 * (кнопки «Сохранить»/«Отмена»), тот же паттерн что и `PreviewConsequencesDialog` для документов.
 * На телефоне таблицы — карточки, диалог вписан в экран, кнопки внизу по 44px. */
export function PaymentPreviewConsequencesDialog({ diff, onResolve }: PaymentPreviewConsequencesDialogProps) {
  const { isMobile } = useBreakpoint();
  const isOpen = diff !== undefined;
  const zIndex = useMemo(() => (isOpen ? aboveEverythingZIndex() : undefined), [isOpen]);
  // Над карточками телефона заголовок раздела — отдельной строкой.
  const sectionTitleStyle = isMobile ? ({ display: 'block', marginBottom: 8 } as const) : undefined;

  return (
    <Modal
      title="Последствия изменения"
      open={isOpen}
      onCancel={() => onResolve(false)}
      footer={<DialogFooter okText="Сохранить" onOk={() => onResolve(true)} onCancel={() => onResolve(false)} />}
      destroyOnHidden
      width={560}
      zIndex={zIndex}
      wrapClassName={isMobile ? 'pay-dialog' : undefined}
    >
      {diff && diff.affectedDocuments.length > 0 ? (
        <>
          <Typography.Text strong style={sectionTitleStyle}>
            Затронутые документы
          </Typography.Text>
          <ResponsiveTable<PaymentPreviewAffectedDocument>
            size="small"
            rowKey="documentId"
            dataSource={diff.affectedDocuments}
            columns={documentColumns}
            pagination={false}
            style={{ marginBottom: 16 }}
          />
        </>
      ) : null}
      {diff && diff.balance ? (
        <>
          <Typography.Text strong style={sectionTitleStyle}>
            Баланс контрагента
          </Typography.Text>
          <ResponsiveTable<PaymentPreviewBalanceChange> size="small" rowKey="counterpartyId" dataSource={[diff.balance]} columns={balanceColumns} pagination={false} />
        </>
      ) : null}
    </Modal>
  );
}
